import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Wifi,
  WifiOff,
  Clock,
  Server,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  Trash2,
  AlertTriangle,
  Wrench,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConfigSyncPanel({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null); // null | 'all' | server_id
  const [expandedLog, setExpandedLog] = useState(null);
  const [whitelistIps, setWhitelistIps] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [savingIps, setSavingIps] = useState(false);
  const [repairingDomain, setRepairingDomain] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/sync/status`, { headers, timeout: 30000 }),
        axios.get(`${API}/admin/sync/logs?limit=10`, { headers }),
      ]);
      setSyncStatus(statusRes.data);
      setSyncLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error("Failed to fetch sync status:", err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchStatus();
    // Fetch whitelisted IPs
    axios
      .get(`${API}/admin/settings`, { headers })
      .then((res) => {
        const ipSetting = res.data?.settings?.find?.(
          (s) => s.key === "global_whitelisted_ips"
        );
        if (ipSetting) setWhitelistIps(ipSetting.ips || []);
      })
      .catch(() => {
        // Try direct endpoint
        axios.get(`${API}/admin/sync/whitelisted-ips`, { headers }).catch(() => {});
      });
  }, [fetchStatus, headers]);

  const triggerSync = async (serverId = null) => {
    const key = serverId || "all";
    setSyncing(key);
    try {
      const url = serverId
        ? `${API}/admin/sync/trigger/${serverId}`
        : `${API}/admin/sync/trigger`;
      const response = await axios.post(url, {}, { headers, timeout: 60000 });
      await fetchStatus();
      if (response.data?.success === false) {
        toast.error("Sync did not verify", { description: response.data?.errors?.[0] || "Review the drift alerts below." });
      } else {
        toast.success("Configuration verified", { description: serverId ? "Agent receipt matched." : "All successful agents returned matching receipts." });
      }
    } catch (err) {
      console.error("Sync failed:", err);
      toast.error("Sync failed", { description: err.response?.data?.detail?.message || err.response?.data?.detail || "The agent did not verify its configuration." });
    } finally {
      setSyncing(null);
    }
  };

  const repairDrift = async (domain) => {
    setRepairingDomain(domain);
    try {
      await axios.post(`${API}/admin/sync/repair/${encodeURIComponent(domain)}`, {}, { headers, timeout: 60000 });
      toast.success("Drift repaired", { description: `${domain} returned a matching config receipt.` });
      await fetchStatus();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error("Repair did not verify", { description: detail?.errors?.[0] || detail?.message || "The persisted agent config still differs." });
      await fetchStatus();
    } finally {
      setRepairingDomain(null);
    }
  };

  const saveWhitelistIps = async () => {
    setSavingIps(true);
    try {
      await axios.post(
        `${API}/admin/sync/whitelisted-ips`,
        { ips: whitelistIps },
        { headers }
      );
      await fetchStatus();
    } catch (err) {
      console.error("Failed to save IPs:", err);
    } finally {
      setSavingIps(false);
    }
  };

  const addIp = () => {
    const ip = newIp.trim();
    if (ip && !whitelistIps.includes(ip)) {
      setWhitelistIps([...whitelistIps, ip]);
      setNewIp("");
    }
  };

  const removeIp = (ip) => {
    setWhitelistIps(whitelistIps.filter((i) => i !== ip));
  };

  const timeAgo = (iso) => {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        <span className="ml-3 text-zinc-400">Loading sync status...</span>
      </div>
    );
  }

  const servers = syncStatus?.servers || [];

  return (
    <div className="admin-section" data-testid="config-sync-panel">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-zinc-800 pb-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase text-indigo-300">Configuration delivery</p>
          <h1 className="mt-1 font-orbitron text-3xl font-bold text-zinc-50" data-testid="config-sync-title">Config Sync Hub</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Audits every 5 minutes. Changes push only through explicit sync or repair actions.
          </p>
        </div>
        <button
          data-testid="sync-all-btn"
          onClick={() => triggerSync()}
          disabled={syncing}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          {syncing === "all" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Sync All Agents
        </button>
      </div>

      <section className="grid grid-cols-2 gap-px overflow-hidden border border-zinc-800 bg-zinc-800 md:grid-cols-4" data-testid="config-sync-metrics">{[["Registered agents", servers.length, "text-zinc-100"], ["Online", servers.filter((server) => server.is_online).length, "text-emerald-300"], ["Hash verified", servers.filter((server) => server.last_sync_hash_verified).length, "text-cyan-300"], ["Drift alerts", syncStatus?.drift_count || 0, (syncStatus?.drift_count || 0) > 0 ? "text-red-300" : "text-emerald-300"]].map(([label, value, tone]) => <div key={label} className="bg-[#121215] p-4"><p className={`font-mono text-2xl font-bold ${tone}`}>{value}</p><p className="mt-1 text-[10px] uppercase text-zinc-600">{label}</p></div>)}</section>

      {(syncStatus?.drift_alerts || []).length > 0 && (
        <section className="border border-red-500/35 bg-red-500/5" data-testid="config-drift-alerts" aria-live="assertive">
          <header className="flex items-start gap-3 border-b border-red-500/20 px-4 py-3"><span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-red-500/10 text-red-300"><AlertTriangle className="h-4 w-4" /></span><div><p className="text-sm font-semibold text-red-200">Configuration drift detected</p><p className="mt-1 text-xs text-zinc-500">These agents persisted values that differ from the dashboard. They are not marked synced.</p></div></header>
          <div className="divide-y divide-red-500/15">{syncStatus.drift_alerts.map((alert) => <div key={alert.id} className="grid gap-3 px-4 py-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center" data-testid={`config-drift-alert-${alert.domain}`}><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="font-mono text-sm font-semibold text-zinc-100" data-testid={`config-drift-domain-${alert.domain}`}>{alert.domain}</p><span className="text-xs text-zinc-600">{alert.server_name}</span></div><div className="mt-2 flex flex-wrap gap-1.5" data-testid={`config-drift-areas-${alert.domain}`}>{(alert.drift_areas || []).map((area) => <span key={area} className="rounded border border-red-500/25 bg-red-500/10 px-2 py-1 text-[10px] font-semibold uppercase text-red-300">{area.replaceAll("_", " ")}</span>)}</div><p className="mt-2 text-xs text-zinc-500">{alert.message}</p></div><button type="button" onClick={() => repairDrift(alert.domain)} disabled={Boolean(repairingDomain)} className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 text-xs font-semibold text-red-200 transition-colors hover:bg-red-500/20 disabled:opacity-50" data-testid={`repair-drift-${alert.domain}`}>{repairingDomain === alert.domain ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}Repair sync</button></div>)}</div>
        </section>
      )}

      {/* Server Cards */}
      <div className="grid gap-4">
        {servers.map((s) => (
          <div
            key={s.server_id}
            data-testid={`sync-server-${s.name}`}
            className="border border-zinc-800 bg-[#121215] p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-2.5 h-2.5 rounded-full ${
                    s.is_online ? "bg-emerald-400 animate-pulse" : "bg-red-400"
                  }`}
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-zinc-400" />
                    <span className="text-white font-medium">{s.name}</span>
                    <span className="text-xs text-zinc-500">{s.ip}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-zinc-400">
                    <span className="flex items-center gap-1">
                      {s.is_online ? (
                        <Wifi className="w-3 h-3 text-emerald-400" />
                      ) : (
                        <WifiOff className="w-3 h-3 text-red-400" />
                      )}
                      {s.is_online ? "Online" : "Offline"}
                    </span>
                    {s.agent_health && (
                      <>
                        <span>{s.agent_health.alias_paths_count} paths</span>
                        <span>{s.agent_health.tenant_count} tenants</span>
                      </>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last sync: {timeAgo(s.last_sync)}
                    </span>
                    {s.last_sync_success !== undefined && (
                      <span
                        className={`flex items-center gap-1 ${
                          s.last_sync_success ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {s.last_sync_success ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        {s.last_sync_success ? "Success" : "Failed"}
                      </span>
                    )}
                    {s.last_sync_hash_verified && <span className="flex items-center gap-1 text-cyan-300" data-testid={`sync-hash-verified-${s.name}`}><ShieldCheck className="h-3 w-3" />Hash verified</span>}
                    {s.drift_count > 0 && <span className="rounded border border-red-500/25 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-red-300" data-testid={`sync-server-drift-${s.name}`}>{s.drift_count} drift</span>}
                  </div>
                </div>
              </div>
              <button
                data-testid={`sync-btn-${s.name}`}
                onClick={() => triggerSync(s.server_id)}
                disabled={syncing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg text-xs font-medium transition disabled:opacity-50"
              >
                {syncing === s.server_id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Sync
              </button>
            </div>
            {s.last_sync_errors?.length > 0 && (
              <div className="mt-3 p-2 bg-red-900/20 border border-red-800/30 rounded-lg">
                <p className="text-xs text-red-400 font-medium mb-1">Errors:</p>
                {s.last_sync_errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-300/80">{e}</p>
                ))}
              </div>
            )}
          </div>
        ))}
        {servers.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            No active agent servers found
          </div>
        )}
      </div>

      {/* Whitelisted IPs */}
      <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-3">
          Global Whitelisted IPs
        </h3>
        <p className="text-xs text-zinc-400 mb-4">
          These IPs bypass all security checks on every agent. Saved and synced automatically.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {whitelistIps.map((ip) => (
            <span
              key={ip}
              className="flex items-center gap-1.5 px-3 py-1 bg-zinc-700/60 border border-zinc-600/40 rounded-full text-xs text-zinc-200"
            >
              {ip}
              <button
                onClick={() => removeIp(ip)}
                className="text-zinc-400 hover:text-red-400 transition"
                data-testid={`remove-whitelist-ip-${ip.replace(/[^a-zA-Z0-9]/g, "-")}`}
                aria-label={`Remove ${ip}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="whitelist-ip-input"
            type="text"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addIp()}
            placeholder="Add IP address..."
            className="flex-1 bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500"
          />
          <button
            onClick={addIp}
            className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm"
            data-testid="add-whitelist-ip-button"
            aria-label="Add whitelist IP"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            data-testid="save-whitelist-btn"
            onClick={saveWhitelistIps}
            disabled={savingIps}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            {savingIps ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5" />
            )}
            Save & Sync
          </button>
        </div>
      </div>

      {/* Sync Logs */}
      <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-3">Sync History</h3>
        <div className="space-y-2">
          {syncLogs.map((log, i) => (
            <div
              key={i}
              className="bg-zinc-900/40 border border-zinc-700/30 rounded-lg p-3"
            >
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedLog(expandedLog === i ? null : i)}
              >
                <div className="flex items-center gap-3 text-xs">
                  <span
                    className={`px-2 py-0.5 rounded-full font-medium ${
                      log.trigger === "periodic"
                        ? "bg-blue-900/40 text-blue-300"
                        : "bg-amber-900/40 text-amber-300"
                    }`}
                  >
                    {log.trigger}
                  </span>
                  <span className="text-zinc-400">
                    {log.success_count}/{log.total_servers} servers synced
                  </span>
                  <span className="text-zinc-500">{timeAgo(log.timestamp)}</span>
                </div>
                {log.results && (
                  expandedLog === i ? (
                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                  )
                )}
              </div>
              {expandedLog === i && log.results && (
                <div className="mt-3 space-y-1.5 border-t border-zinc-700/30 pt-3">
                  {log.results.map((r, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs">
                      {r.success ? (
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                      ) : (
                        <XCircle className="w-3 h-3 text-red-400" />
                      )}
                      <span className="text-zinc-300">
                        {r.name || r.server_id}
                      </span>
                      {r.config_pushed && (
                        <span className="text-zinc-500">
                          config:{r.alias_paths_count} paths
                        </span>
                      )}
                      {r.tenants_pushed > 0 && (
                        <span className="text-zinc-500">
                          tenants:{r.tenants_pushed}
                        </span>
                      )}
                      {r.tenants_verified > 0 && <span className="text-cyan-400">verified:{r.tenants_verified}</span>}
                      {r.errors?.length > 0 && (
                        <span className="text-red-400 truncate max-w-xs">
                          {r.errors[0]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {syncLogs.length === 0 && (
            <p className="text-center py-6 text-zinc-500 text-sm">
              No sync logs yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
